import { withNamespaces } from 'react-i18next';
import { Button, Input } from 'reactstrap';

const SETUP_STEPS = [
    {
        titleKey: 'Create an App Registration',
        blocks: [
            { text: 'In the Azure Portal, go to:' },
            { text: 'Microsoft Entra ID → App registrations → New registration' },
            { text: 'Create the application inside your organization\'s Microsoft Entra tenant.' },
            { text: 'After creating the application, keep the following information available:' },
            { items: ['Application (client) ID', 'Directory (tenant) ID'] },
            { text: 'You will need these values later when configuring the mailbox connection in SIP.' },
        ],
    },
    {
        titleKey: 'Add the Required Delegated Permissions',
        blocks: [
            { text: 'Open your newly created App Registration:' },
            { text: 'Microsoft Entra ID → App registrations → [Your Application]' },
            { text: 'Then go to:' },
            { text: 'API permissions → + Add a permission' },
            { heading: 'Add Exchange Online permissions' },
            { text: 'Select APIs my organization uses.' },
            { text: 'Search for:' },
            { text: 'Office 365 Exchange Online' },
            { text: 'Select Delegated permissions.' },
            { text: 'Search for and enable the following permissions:' },
            { items: ['IMAP.AccessAsUser.All', 'SMTP.Send'], code: true },
            { text: 'Click Add permissions.' },
            { heading: 'Add the offline_access permission' },
            { text: 'The offline_access permission is added through Microsoft Graph.' },
            { text: 'Go again to:' },
            { text: 'API permissions → + Add a permission' },
            { text: 'Then:' },
            { text: 'Select Microsoft Graph.' },
            { text: 'Select Delegated permissions.' },
            { text: 'Search for:' },
            { items: ['offline_access'], code: true },
            { text: 'Select it and click Add permissions.' },
            { text: 'Your application should now have these delegated permissions:' },
            { items: ['IMAP.AccessAsUser.All', 'SMTP.Send', 'offline_access'], code: true },
            { text: 'If your organization\'s Microsoft Entra policies require administrator approval, click:' },
            { text: 'Grant admin consent for [Your Organization]' },
            { text: 'An administrator of your Microsoft Entra tenant may need to perform this step.' },
        ],
    },
    {
        titleKey: 'Create a Client Secret',
        blocks: [
            { text: 'Go to:' },
            { text: 'Certificates & secrets → Client secrets → New client secret' },
            { text: 'Enter a description for the secret.' },
            { text: 'Select an appropriate expiration period.' },
            { text: 'Click Add.' },
            { text: 'Copy the Value immediately after creating the secret.' },
            { text: 'Important: Copy the Value, not the Secret ID.', emphasis: true },
            { text: 'The secret value is only displayed once and cannot be retrieved later.' },
            { text: 'You will need this value when configuring SIP.' },
        ],
    },
    {
        titleKey: 'Add the Redirect URI',
        blocks: [
            { text: 'Inside the App Registration, go to:' },
            { text: 'Authentication → Add a platform → Web' },
            { text: 'Enter the Redirect URI provided in the SIP Mailer Settings.' },
            { redirect: true },
            { text: 'Then save the configuration.' },
            { text: 'Make sure the Redirect URI is entered exactly as shown in SIP.' },
        ],
    },
    {
        titleKey: 'Configure the Mailbox in SIP',
        blocks: [
            { text: 'Return to:' },
            { text: 'SIP → Mailer Settings' },
            { text: 'Enter the following values from your Microsoft Entra App Registration:' },
            {
                items: [
                    'Tenant ID → Directory (tenant) ID',
                    'Application / Client ID → Application (client) ID',
                    'Client Secret → The secret Value created in Step 3',
                ],
            },
            { text: 'Click Save.' },
            { text: 'Then click:' },
            { text: 'Connect Microsoft' },
            { text: 'You will be redirected to Microsoft to sign in and authorize the mailbox connection.' },
            { text: 'After successful authorization, the mailbox will be connected to SIP and can be used for email synchronization, sending emails, and receiving emails through the SIP Mailer.' },
        ],
    },
];

const EntraSetupDrawer = ({
    t,
    isOpen,
    redirectUri,
    onClose,
    onCopyRedirectUri,
}) => (
    <>
        <div
            className={`offcanvas offcanvas-end mailer-settings__drawer${isOpen ? ' show' : ''}`}
            tabIndex="-1"
            aria-hidden={!isOpen}
        >
            <div className="offcanvas-header border-bottom">
                <h5 className="offcanvas-title font-size-16">
                    {t('Microsoft Entra Setup')}
                </h5>
                <Button
                    type="button"
                    close
                    aria-label={t('Close')}
                    onClick={onClose}
                />
            </div>

            <div className="offcanvas-body">
                <p className="text-muted font-size-13">
                    {t('Each customer organization must use its own Microsoft Entra App Registration. SIP does not use a shared Entra application.')}
                </p>

                <ol className="list-unstyled mb-4">
                    {SETUP_STEPS.map((step, index) => (
                        <li key={step.titleKey} className="d-flex mb-3">
                            <span className="mailer-settings__step-number bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 font-size-12 fw-semibold">
                                {index + 1}
                            </span>
                            <div>
                                <div className="fw-semibold font-size-13">
                                    {t(step.titleKey)}
                                </div>
                                {step.blocks.map((block, blockIndex) => {
                                    if (block.heading) {
                                        return (
                                            <div key={block.heading} className="fw-semibold font-size-12 mb-1">
                                                {t(block.heading)}
                                            </div>
                                        );
                                    }

                                    if (block.items) {
                                        return (
                                            <ul key={`${step.titleKey}-list-${blockIndex}`} className="text-muted font-size-12 mb-1 ps-3">
                                                {block.items.map((item) => (
                                                    <li key={item}>
                                                        {block.code ? <code>{item}</code> : t(item)}
                                                    </li>
                                                ))}
                                            </ul>
                                        );
                                    }

                                    if (block.redirect) {
                                        return (
                                            <div key="redirect-uri" className="mb-1">
                                                <div className="fw-semibold font-size-13 mb-1">
                                                    {t('Redirect URI')}
                                                </div>
                                                {redirectUri ? (
                                                    <div className="d-flex align-items-center">
                                                        <Input
                                                            type="text"
                                                            className="form-control font-size-12"
                                                            value={redirectUri}
                                                            readOnly
                                                        />
                                                        <Button
                                                            type="button"
                                                            color="light"
                                                            className="ms-2 text-nowrap"
                                                            onClick={onCopyRedirectUri}
                                                        >
                                                            <i className="ri-file-copy-line align-middle me-1" />
                                                            {t('Copy')}
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <div className="text-muted font-size-12">
                                                        {t('Redirect URI is not configured on the server.')}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }

                                    return (
                                        <div
                                            key={`${step.titleKey}-text-${blockIndex}`}
                                            className={block.emphasis ? 'fw-semibold font-size-12 mb-1' : 'text-muted font-size-12 mb-1'}
                                        >
                                            {t(block.text)}
                                        </div>
                                    );
                                })}
                            </div>
                        </li>
                    ))}
                </ol>
            </div>
        </div>

        {isOpen && (
            <div
                className="offcanvas-backdrop fade show"
                onClick={onClose}
            />
        )}
    </>
);

export default withNamespaces()(EntraSetupDrawer);
